import _ from "lodash";
import client from "lib/apolloClient/client";
import {
  getClassMaterialsFeedQuery,
  getClassMaterialsOutlineFeedQuery,
  getSyncStatusQuery,
  getEntireClassFlowFeedQuery,
  getUserCoursesV2Query,
} from "./ClassMaterialsQuery";
import { classMaterialsFragmentWithoutDepth } from "./ClassMaterialsFragments";
import { getIsNewExpandCollapseBehaviourEnabled } from "./ClassMaterialsModule";

export const getClassMaterialsFeedFromCache = (variables, queryParams = {}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getClassMaterialsFeedQuery,
      variables,
      ...queryParams,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getEntireClassMaterialsFeedFromCache = (
  variables,
  queryParams = {}
) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getEntireClassFlowFeedQuery,
      variables,
      ...queryParams,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getClassMaterialsOutlineFeedFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getClassMaterialsOutlineFeedQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const writeClassMaterialsOutlineFeedToCache = ({ data, variables }) => {
  client.writeQuery({
    query: getClassMaterialsOutlineFeedQuery,
    variables,
    data: {
      node: {
        ...data,
      },
    },
  });
};

export const writeClassMaterialsFeedToCache = ({
  courseId,
  data,
  variables,
}) => {
  client.writeQuery({
    query: getClassMaterialsFeedQuery,
    variables,
    data: {
      node: {
        id: courseId,
        organizationResourcesFlatFeed: data,
        __typename: "Course",
      },
    },
  });
};

export const writeClassMaterialsEntireFeedToCache = ({
  courseId,
  data,
  variables,
}) => {
  client.writeQuery({
    query: getEntireClassFlowFeedQuery,
    variables,
    data: {
      node: {
        id: courseId,
        organizationResourcesFlatFeed: data,
        __typename: "Course",
      },
    },
  });
};

export const getClassMaterialsSyncStatusFromCache = ({ variables }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getSyncStatusQuery,
      variables,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getClassMaterialItemFromCache = ({ id, variables }) => {
  let fragmentData = {};
  try {
    fragmentData = client.readFragment({
      id: `OrganizationResource:${id}`,
      fragment: classMaterialsFragmentWithoutDepth.classMaterialsWithChildren,
      fragmentName: "classMaterialsWithChildrenItem",
      variables,
    });
  } catch (e) {
    return {};
  }
  return fragmentData;
};

export const writeClassMaterialsItemToCache = ({ id, variables, data }) => {
  let fragmentData = {};
  try {
    fragmentData = client.writeFragment({
      id: `OrganizationResource:${id}`,
      fragment: classMaterialsFragmentWithoutDepth.classMaterialsWithChildren,
      fragmentName: "classMaterialsWithChildrenItem",
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
  return fragmentData;
};

/**
 * For backward compatibility, these functions removes unneccesary fields
 * from feed so that it corresponds to the structure of new query
 */
const getFilteredClassMaterialsFeed = (feed = {}) => {
  const edges = _.get(feed, "edges", []);

  const updatedEdges = _.map(edges, edge => {
    const { node } = edge;

    const childrenEdges = _.get(node, "childrenV2.edges", []);

    const updatedChildrenEdges = _.map(childrenEdges, childEdge => {
      const childNode = _.get(childEdge, "node", {});
      return {
        ...childEdge,
        node: _.pick(childNode, ["id", "__typename"]),
      };
    });

    const updatedNode = {
      ..._.pick(node, [
        "id",
        "label",
        "depth",
        "resourceType",
        "parent",
        "childrenHierarchyInfo",
        "item",
        "__typename",
      ]),
      childrenV2: {
        edges: updatedChildrenEdges,
        __typename: "OrganizationResourceConnection",
      },
    };

    if (!_.isEmpty(updatedNode.item)) {
      updatedNode["item"] = _.pick(updatedNode.item, ["id", "__typename"]);
    }

    return {
      ...edge,
      node: updatedNode,
    };
  });

  return {
    ...feed,
    edges: updatedEdges,
  };
};

const updateAndWriteEntireFeedToCache = ({ courseId, data, variables }) => {
  const updatedFeed = getFilteredClassMaterialsFeed(data);

  writeClassMaterialsEntireFeedToCache({
    courseId,
    variables,
    data: updatedFeed,
  });
};

export const getClassMaterialsFeedReadAndWriteHelpers = () => {
  if (getIsNewExpandCollapseBehaviourEnabled()) {
    return {
      getClassMaterialsFeed: getEntireClassMaterialsFeedFromCache,
      writeClassMaterialsFeed: updateAndWriteEntireFeedToCache,
    };
  }

  return {
    getClassMaterialsFeed: getClassMaterialsFeedFromCache,
    writeClassMaterialsFeed: writeClassMaterialsFeedToCache,
  };
};

export const getUserCoursesV2FromCache = ({
  userId,
  first,
  courseFilters,
  courseGradeFilters,
  archivedCourseFilters,
  projectGroupFilters,
}) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getUserCoursesV2Query,
      variables: {
        userId,
        first,
        courseFilters,
        courseGradeFilters,
        archivedCourseFilters,
        projectGroupFilters,
      },
    });
  } catch (e) {
    return {};
  }
  return _.get(queryData, "node", {});
};
